// src/features/locker-assignments/components/LockerAssignmentEdit.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Edit, 
  SimpleForm, 
  useGetOne,
  useNotify,
  useRedirect,
  useTranslate,
  useDataProvider,
  TextInput,
  DateTimeInput,
  required,
  useGetRecordId,
} from 'react-admin';
import { useFormContext } from 'react-hook-form'; // ← AÑADIR ESTA IMPORTACIÓN
import {
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Chip,
} from '@mui/material';
import { 
  Schedule as ScheduleIcon,
  Archive as ArchiveIcon,
  Note as NoteIcon,
} from '@mui/icons-material';

import { MyToolbar, MyFormContainer, MyFormSection } from '@/components';
import { useLockerAssignmentPermissions } from '../hooks';
import { FormValidationHandler } from './forms/formValidation';
import { TagsSection } from './forms/TagsSection';
import { LockerRequirementsSection } from './forms/LockerRequirementsSection';
import { ZonesSection } from './forms/ZonesSection';
import ManageTagsDialog from './ManageTagsDialog';
import type {
  LockerAssignmentDetailResponse,
  Hotel,
  LockerType,
  Zone,
  ReferenceField,
  LockerRequirement,
  Tag,
  TagType,
  ValidationErrors,
  ReferenceSummary,
  AssignmentStatus,
} from '../types';

// ============================================
// NUEVO: Componente interno para acceder al FormContext
// ============================================
const FormSynchronizer: React.FC<{
  lockerRequirements: LockerRequirement[];
  selectedZones: string[];
  referenceValues: Record<string, any>;
}> = ({ lockerRequirements, selectedZones, referenceValues }) => {
  const { setValue } = useFormContext();

  // Sincronizar lockerRequirements con el formulario
  useEffect(() => {
    setValue('_lockerRequirements', JSON.stringify(lockerRequirements), {
      shouldDirty: true,  // ← CRÍTICO: marca el formulario como modificado
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [lockerRequirements, setValue]);

  // Sincronizar selectedZones con el formulario
  useEffect(() => {
    setValue('_selectedZones', JSON.stringify(selectedZones), {
      shouldDirty: true,  // ← CRÍTICO
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [selectedZones, setValue]);

  // Sincronizar referenceValues con el formulario
  useEffect(() => {
    setValue('_referenceValues', JSON.stringify(referenceValues), {
      shouldDirty: true,  // ← CRÍTICO
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [referenceValues, setValue]);

  return null; // No renderiza nada
};

const LockerAssignmentEdit: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const permissions = useLockerAssignmentPermissions();
  const recordId = useGetRecordId();

  // ============================================
  // OBTENER DATOS DE LA ASIGNACIÓN
  // ============================================
  
  const { data: assignment, isLoading: loadingAssignment, error: assignmentError } = useGetOne<LockerAssignmentDetailResponse>(
    'locker-assignments',
    { id: recordId }
  );

  // ============================================
  // ESTADO
  // ============================================
  
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [lockerTypes, setLockerTypes] = useState<LockerType[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [referenceFields, setReferenceFields] = useState<ReferenceField[]>([]);
  const [lockerRequirements, setLockerRequirements] = useState<LockerRequirement[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [referenceValues, setReferenceValues] = useState<Record<string, any>>({});
  const [loadingHotelData, setLoadingHotelData] = useState(false);
  const [accessTags, setAccessTags] = useState<Tag[]>([]);
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [availableTagTypes, setAvailableTagTypes] = useState<TagType[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isEditAllowed, setIsEditAllowed] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);

  // ============================================
  // VALORES POR DEFECTO BASADOS EN LA ASIGNACIÓN
  // ============================================
  
  const defaultValues = useMemo(() => {
    if (!assignment || !isDataReady) return {};
    
    const formatDateForInput = (dateValue: string | Date | null | undefined) => {
      if (!dateValue) return null;
      
      if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      if (dateValue instanceof Date) {
        return dateValue.toISOString();
      }
      
      return null;
    };
    
    return {
      startsAt: formatDateForInput(assignment.startsAt),
      expiresAt: formatDateForInput(assignment.expiresAt),
      notes: assignment.observations || assignment.notes || '',
      // Campos ocultos para sincronización
      _lockerRequirements: JSON.stringify(lockerRequirements),
      _selectedZones: JSON.stringify(selectedZones),
      _referenceValues: JSON.stringify(referenceValues),
    };
  }, [assignment, isDataReady, lockerRequirements, selectedZones, referenceValues]);

  // ============================================
  // VERIFICAR SI LA EDICIÓN ESTÁ PERMITIDA
  // ============================================
  
  useEffect(() => {
    if (assignment) {
      const editableStatuses: AssignmentStatus[] = [
        'Pending' as AssignmentStatus,
        'Active' as AssignmentStatus,
      ];
      setIsEditAllowed(editableStatuses.includes(assignment.status as AssignmentStatus));
      
      if (assignment.hotel) {
        setHotel(assignment.hotel as Hotel);
      }
    }
  }, [assignment]);

  // ============================================
  // CARGAR DATOS DEL HOTEL
  // ============================================
  
  useEffect(() => {
    if (!assignment?.hotel?.id) return;

    const loadHotelData = async () => {
      setLoadingHotelData(true);
      setIsDataReady(false);
      const hotelId = assignment.hotel.id;
      
      try {
        try {
          const hotelDetailResponse = await (dataProvider as any).getGeneralConfiguration(hotelId);
          const supportedTagTypes = hotelDetailResponse.data?.supportedTagTypes || [];
          setAvailableTagTypes(supportedTagTypes);
        } catch (error) {
          console.warn('Error loading hotel tag types:', error);
          setAvailableTagTypes([]);
        }

        const typesResponse = await dataProvider.getList('locker-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: { hotelId: hotelId },
        });
        setLockerTypes(typesResponse.data);

        const zonesResponse = await dataProvider.getList('zones', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: { hotelId: hotelId },
        });
        setZones(zonesResponse.data.filter((z: Zone) => z.isActive));

        try {
          const fieldsResponse = await (dataProvider as any).getReferenceFields(hotelId);
          const fields = fieldsResponse.data?.referenceFields || fieldsResponse.data?.fields || [];
          
          if (fields.length > 0) {
            const sortedFields = [...fields].sort((a: any, b: any) => 
              (a.displayOrder || 0) - (b.displayOrder || 0)
            );
            setReferenceFields(sortedFields);
          } else {
            if (assignment?.references && assignment.references.length > 0) {
              const fieldsFromReferences = assignment.references.map((ref: any) => ({
                key: ref.key,
                label: ref.label || ref.key,
                isMandatory: false,
                fieldType: 'text' as const,
                defaultValue: '',
              }));
              setReferenceFields(fieldsFromReferences);
            } else {
              setReferenceFields([{
                key: 'reference',
                label: 'Referencia',
                isMandatory: false,
                fieldType: 'text',
                defaultValue: '',
              }]);
            }
          }
        } catch (error) {
          console.warn('Error loading reference fields:', error);
          if (assignment?.references && assignment.references.length > 0) {
            const fieldsFromReferences = assignment.references.map((ref: any) => ({
              key: ref.key,
              label: ref.label || ref.key,
              isMandatory: false,
              fieldType: 'text' as const,
              defaultValue: '',
            }));
            setReferenceFields(fieldsFromReferences);
          } else {
            setReferenceFields([{
              key: 'reference',
              label: 'Referencia',
              isMandatory: false,
              fieldType: 'text',
              defaultValue: '',
            }]);
          }
        }

        if (assignment?.tags && assignment.tags.length > 0) {
          const processedTags = assignment.tags.map((tag: any) => ({
            id: tag.id,
            code: tag.data || tag.code,
            data: tag.data,
            typeCode: tag.typeCode,
            typeName: availableTagTypes.find(t => t.typeCode === tag.typeCode)?.name || tag.typeCode,
            description: tag.description,
            status: tag.isActive ? 'Active' : 'Inactive',
            createdAt: tag.createdAt,
          }));
          setAccessTags(processedTags);
        }

        setIsDataReady(true);

      } catch (error) {
        notify(translate('resources.locker-assignments.edit.error_loading_hotel_data'), { type: 'error' });
        console.error('Error loading hotel data:', error);
        setIsDataReady(true);
      } finally {
        setLoadingHotelData(false);
      }
    };

    loadHotelData();
  }, [assignment, dataProvider, notify, translate]);

  // ============================================
  // INICIALIZAR VALORES BASADOS EN LA ASIGNACIÓN
  // ============================================
  
  useEffect(() => {
    if (!assignment || lockerTypes.length === 0) return;

    const requirements: LockerRequirement[] = lockerTypes.map((type) => {
      const requested = assignment.requestedLockerTypes?.find(
        rt => rt.typeId === type.id || rt.typeName === type.name
      );
      return {
        lockerTypeId: type.id,
        typeName: type.name,
        quantity: requested?.requestedCount || 0,
      };
    });
    setLockerRequirements(requirements);

    if (assignment.allowedZones && assignment.allowedZones.length > 0) {
      const zoneIds = assignment.allowedZones.map((zone: any) => {
        if (typeof zone === 'string') return zone;
        if (zone.id) return zone.id;
        if (zone.zoneId) return zone.zoneId;
        return null;
      }).filter(Boolean);
      
      setSelectedZones(zoneIds);
    } else if (zones.length > 0) {
      setSelectedZones(zones.map(z => z.id));
    }

    if (assignment.references && assignment.references.length > 0) {
      const values: Record<string, any> = {};
      assignment.references.forEach((ref: any) => {
        values[ref.key] = ref.value || '';
      });
      setReferenceValues(values);
    }
  }, [assignment, lockerTypes, zones]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleLockerRequirementChange = (index: number, value: number) => {
    const updated = [...lockerRequirements];
    updated[index] = {
      ...updated[index],
      quantity: value,
    };
    setLockerRequirements(updated);
    
    if (validationErrors.lockerRequirements && value > 0) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.lockerRequirements;
        return newErrors;
      });
    }
  };

  const handleReferenceValueChange = (key: string, value: any) => {
    setReferenceValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleZoneChange = (event: any) => {
    const value = event.target.value;
    setSelectedZones(typeof value === 'string' ? value.split(',') : value);
  };

  const handleTagsUpdated = (updatedTags: Tag[]) => {
    setAccessTags(updatedTags);
  };

  const handleAddTag = () => {
    setOpenTagsDialog(true);
  };

  const handleRemoveTag = (index: number) => {
    setAccessTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    redirect('/locker-assignments');
  };

  const getReferenceString = () => {
    return assignment?.displayReference || 
      referenceFields
        .map(field => referenceValues[field.key])
        .filter(value => value)
        .join(' - ') || 
      'Asignación';
  };

  // ============================================
  // VALIDACIÓN
  // ============================================

  const validateForm = (values: any) => {
    const errors: ValidationErrors = {};
    
    const hasAtLeastOneLocker = lockerRequirements.some(req => req.quantity > 0);
    if (!hasAtLeastOneLocker) {
      errors.lockerRequirements = translate('resources.locker-assignments.edit.validation.locker_required');
      setValidationErrors({ lockerRequirements: translate('resources.locker-assignments.edit.validation.locker_required') });
    } else {
      setValidationErrors({});
    }
    
    referenceFields.forEach(field => {
      if (field.isMandatory && !referenceValues[field.key]) {
        errors[`reference_${field.key}`] = translate('resources.locker-assignments.edit.validation.field_required', { field: field.label });
      }
    });
    
    return errors;
  };

  // ============================================
  // SUBMIT
  // ============================================

  const handleSubmit = async (data: any) => {
     console.log('🔵 TRANSFORM EJECUTADO - Este mensaje debería aparecer ANTES del PUT');
  console.log('📦 Data recibida en transform:', data);
    const { 
      startsAt,
      expiresAt, 
      notes,
      _lockerRequirements,
      _selectedZones,
      _referenceValues,
      _formInitialized, 
      _initialized, 
      _ready,
      ...rest 
    } = data;
    
    // Parsear los valores sincronizados desde los campos ocultos
    const finalLockerRequirements = _lockerRequirements 
      ? JSON.parse(_lockerRequirements) 
      : lockerRequirements;
      
    const finalSelectedZones = _selectedZones 
      ? JSON.parse(_selectedZones) 
      : selectedZones;
      
    const finalReferenceValues = _referenceValues 
      ? JSON.parse(_referenceValues) 
      : referenceValues;
    
    const references: ReferenceSummary[] = referenceFields
      .map(field => ({
        key: field.key,
        value: finalReferenceValues[field.key] || '',
      }))
      .filter(ref => ref.value !== '');

    const formData = {
      expiresAt: expiresAt || null,
      observations: notes || '',
      requestedLockerTypes: finalLockerRequirements
        .filter((req: LockerRequirement) => req.quantity > 0)
        .map((req: LockerRequirement) => ({
          lockerTypeId: req.lockerTypeId,
          requestedCount: req.quantity,
        })),
      references: references,
    };
    
    console.log('📤 Sending to backend:', formData);
    
    return formData;
  };

  const handleSuccess = async () => {
     console.log('✅ SUCCESS CALLBACK');
    notify(translate('resources.locker-assignments.edit.success'), { type: 'success' });
    redirect('/locker-assignments');
  };

  // ============================================
  // RENDERIZADO
  // ============================================

  if (loadingAssignment || loadingHotelData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (assignmentError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error al cargar la asignación: {assignmentError.message}
        </Alert>
      </Box>
    );
  }

  if (!permissions.canEdit) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {translate('resources.locker-assignments.edit.no_permission')}
        </Alert>
      </Box>
    );
  }

  if (!isEditAllowed) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {translate('resources.locker-assignments.edit.not_editable_status')}
        </Alert>
      </Box>
    );
  }

  if (!isDataReady) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleError = (error: any) => {
  console.error('❌ ERROR CALLBACK:', error);
  notify(error.message || 'Error updating assignment', { type: 'error' });
};

  return (
    <Edit 
      title={translate('resources.locker-assignments.titles.edit')}
      redirect={false}
      mutationMode="pessimistic" 
      mutationOptions={{
        onSuccess: handleSuccess,
         onError: handleError
      }}
      transform={handleSubmit}
    >
      <MyFormContainer>
        <SimpleForm
          toolbar={
            <MyToolbar 
              onCancel={handleCancel} 
              resource="locker-assignments"
            />
          }
          validate={validateForm}
          defaultValues={defaultValues}
          key={`form-${assignment?.id}-${isDataReady}`}
        >
          {/* ✅ NUEVO: Componente que sincroniza el estado con React Admin */}
          <FormSynchronizer
            lockerRequirements={lockerRequirements}
            selectedZones={selectedZones}
            referenceValues={referenceValues}
          />
          
          <FormValidationHandler 
            selectedHotel={hotel}
            lockerRequirements={lockerRequirements}
            referenceFields={referenceFields}
            referenceValues={referenceValues}
          />
          
          {/* Información del Hotel y Referencias */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2, p: 2 }}>
            <Grid item xs={12} md={2}>
              <Box sx={{ p: 2, borderRadius: 1 }}>
                <Typography color="text.secondary">Hotel</Typography>
                <Typography>{assignment?.hotel?.name || 'Cargando...'}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              {referenceFields.length > 0 && (
                <Box>
                  <Grid container spacing={2}>
                    {referenceFields.map((field, index) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        key={`${field.key}_${index}`}
                      >
                        <TextField
                          label={field.label}
                          value={referenceValues[field.key] || ''}
                          onChange={(e) => handleReferenceValueChange(field.key, e.target.value)}
                          type={field.fieldType === 'number' ? 'number' : 'text'}
                          variant="outlined"
                          size="small"
                          fullWidth
                          required={field.isMandatory}
                          InputProps={{
                            autoComplete: 'off',
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            {assignment?.assignedLockers && assignment.assignedLockers.length > 0 && (
              <Grid item xs={12} md={2}>
                {assignment.assignedLockers.map((locker: any, index: number) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      mb: 1 
                    }}
                  >
                    <Chip label={`${locker.displayNumber} - ${locker.type}`} />
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      <em>({locker.zone})</em>
                    </Typography>
                  </Box>
                ))}
              </Grid>
            )}
          </Grid>

          {hotel && (
            <>
              {/* Fechas y Tags */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
                  <MyFormSection title="Assignment Details" icon={<ScheduleIcon />}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <DateTimeInput
                          source="startsAt"
                          label="Start Date"
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <DateTimeInput
                          source="expiresAt"
                          label="End Date"
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </MyFormSection>
                </Grid>

                <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                  <TagsSection
                    accessTags={accessTags}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                  />
                </Grid>
              </Grid>

              {/* Requerimientos, Zonas y Notas */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <LockerRequirementsSection
                    lockerRequirements={lockerRequirements}
                    loadingHotelData={loadingHotelData}
                    validationErrors={validationErrors}
                    onLockerRequirementChange={handleLockerRequirementChange}
                    setValidationErrors={setValidationErrors}
                  />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <ZonesSection
                    zones={zones}
                    selectedZones={selectedZones}
                    loadingHotelData={loadingHotelData}
                    onZoneChange={handleZoneChange}
                  />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <MyFormSection title={translate('resources.locker-assignments.messages.notes_title')} icon={<NoteIcon />}>
                    <TextInput
                      source="notes"
                      label=""
                      placeholder={translate('resources.locker-assignments.messages.notes_placeholder')}
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                    />
                  </MyFormSection>
                </Grid>
              </Grid>
            </>
          )}
        </SimpleForm>
      </MyFormContainer>

      <ManageTagsDialog
        open={openTagsDialog}
        onClose={() => setOpenTagsDialog(false)}
        assignmentId={String(assignment?.id) || ''}
        assignmentReference={getReferenceString()}
        currentTags={accessTags}
        onTagsUpdated={handleTagsUpdated}
        hotelId={assignment?.hotel?.id || ''}
      />
    </Edit>
  );
};

export default LockerAssignmentEdit;